import { ConditionalTooltip } from '@/components/utils/conditionalComponents';
import { ClickHandler } from '@/utils/clickEvent';
import { Component, ReturnComponent, SxStyle } from '@/utils/reactTypes';
import { Box, Button } from '@mui/material';
import { ReactNode, forwardRef, useMemo } from 'react';
import { Link, To } from 'react-router';

export const AxleButton = forwardRef(
    (
        {
            children,
            onClick,
            startIcon,
            disabled = false,
            tooltipWhenDisabled = '',
            size = 'medium',
            color = 'primary',
            className = '',
            variant = 'contained',
            wrap = true,
            sx = {},
            href,
            target,
            rel,
            to,
            linkState,
            ...rest
        }: {
            children: ReactNode;
            onClick?: ClickHandler;
            startIcon?: Component | undefined;
            disabled?: boolean;
            tooltipWhenDisabled?: string;
            wrap?: boolean;
            size?: 'small' | 'medium' | 'large';
            color?: 'primary' | 'secondary';
            className?: string; // TODO: Deprecate this property.
            variant?: 'text' | 'outlined' | 'contained'; // TODO: Deprecate this property.
            sx?: SxStyle;
            href?: string;
            target?: string;
            rel?: string;
            to?: To;
            linkState?: unknown;
        },
        ref,
    ): ReturnComponent => {
        const buttonSx = useMemo(
            () => ({ whiteSpace: wrap ? undefined : 'nowrap', lineHeight: '1.5em' }),
            [wrap],
        );
        // Without this <Box/> wrapper, the children get affected by the horizontal
        //  inline-flex layout of the Button component.
        const content = <Box>{children}</Box>;
        return (
            <Box
                className={className}
                // TODO: Can we get rid of this?
                sx={{
                    position: 'relative',
                    ...sx,
                }}
                // We need the forwardRef and the {...rest} props to make the button work with
                //  a surrounding <Tooltip> component. See: https://github.com/mui/material-ui/issues/21324#issuecomment-639830442
                ref={ref}
                {...rest}
            >
                <ConditionalTooltip title={tooltipWhenDisabled} condition={disabled}>
                    {/* This <Box/> wrapper is necessary as <Tooltip/> doesn’t work when directly
                        applied to a button if the button is disabled. */}
                    <Box>
                        {to !== undefined ? (
                            <Button
                                onClick={onClick}
                                startIcon={startIcon}
                                variant={variant}
                                color={color}
                                disabled={disabled}
                                size={size}
                                sx={buttonSx}
                                component={Link}
                                to={to}
                                state={linkState}
                                target={target}
                                rel={rel}
                            >
                                {content}
                            </Button>
                        ) : (
                            <Button
                                onClick={onClick}
                                startIcon={startIcon}
                                variant={variant}
                                color={color}
                                disabled={disabled}
                                size={size}
                                sx={buttonSx}
                                {...(href !== undefined && { href, target, rel })}
                            >
                                {content}
                            </Button>
                        )}
                    </Box>
                </ConditionalTooltip>
            </Box>
        );
    },
);
AxleButton.displayName = 'AxleButton';
